/**
 * Fasce orarie stagionali per escursioni/esperienze.
 *
 * Un'escursione può avere fasce di partenza diverse per periodo dell'anno
 * (es. Obsidian: pomeriggio 15:00 in bassa stagione, 14:00 + sunset in alta).
 * Modello: array di periodi (MM-DD start/end, con wrap-around capodanno), ognuno
 * con la sua lista di fasce. Quando `seasonal_periods` è valorizzato la
 * generazione slot è period-aware; altrimenti fallback alla lista piatta
 * `default_time_slots` (retrocompat, nessuna rottura).
 *
 * I periodi possono essere definiti a livello azienda (users.seasonal_periods,
 * preset riusabile) e copiati/modificati per singola escursione.
 */

import type { ExcursionPricingTier } from '@/lib/pricing/excursion-adapter';

export interface SeasonalSlot {
  start_time: string;        // 'HH:MM'
  duration_minutes: number;  // 0 = eredita durata escursione
  capacity?: number;         // 0/undefined = eredita max_capacity
  // Prezzo della FASCIA (granularità: prezzo per fascia, non per stagione). Vedi
  // [[feedback_granularity_priority]]. LEGACY condiviso: fallback quando i campi
  // per-modalità sotto sono assenti (dati pre-split). 0/undefined = non prezzato.
  price?: number;
  /** Prezzo per-fascia a PERSONA (pricing_mode='per_person', × persone al booking).
   *  Indipendente da price_group. Assente → fallback a `price`. */
  price_per_person?: number;
  /** Prezzo per-fascia a GRUPPO flat (pricing_mode='per_group', non × persone).
   *  Indipendente da price_per_person. Assente → fallback a `price`. */
  price_group?: number;
  id?: string;               // id stabile (fasce daily auto-generate) — per versioning Fase B
  /** SOLO barche multi-modalità: modalità di noleggio a cui appartiene la fascia
   *  ('daily'|'hourly'). Assente = 'daily' (retro-compat). Le fasce daily non vanno
   *  confuse con quelle hourly. Vedi [[project_boat_span_pricing]]. */
  rental_mode?: string;
  label?: string;            // es. 'Mattina', 'Pomeriggio', 'Giornata intera'
  status?: 'active' | 'deprecated' | 'archived'; // Fase B: ciclo-vita fascia (default active)
  /** Tariffe per numero persone (pricing_mode='tiered') PROPRIE di questa fascia
   *  oraria. È il livello più granulare (Stagione→Fascia→prezzo): vince sul
   *  season-level `SeasonalPeriod.pricing_tiers`. Assente/vuoto = fallback stagione
   *  → listino statico escursione. Vedi tiersForDate(periods, md, startTime). */
  pricing_tiers?: ExcursionPricingTier[];
}

export interface MdRange {
  start_md: string;          // 'MM-DD' inclusivo
  end_md: string;            // 'MM-DD' inclusivo (wrap se end < start)
}

export interface SeasonalPeriod {
  label?: string;            // es. "Alta stagione"
  /** Intervalli MM-DD del periodo (multi-intervallo: una stagione può avere più
   *  finestre, es. Bassa = apr-mag + ott). Canonico. */
  ranges?: MdRange[];
  /** Legacy/mirror del primo range (retrocompat display). */
  start_md: string;
  end_md: string;
  slots: SeasonalSlot[];
  /** Prezzo base (tariffa vendor) per questo periodo. Override del prezzo fisso
   *  dell'escursione quando la partenza cade in questa finestra. Semantica
   *  dipende dal pricing_mode (per_group=prezzo gruppo, per_person=a persona). */
  price?: number;
  price_half_day?: number;   // opzionale, per partenze mezza giornata
  /** Tariffe per numero persone (pricing_mode='tiered') PROPRIE di questa stagione.
   *  Assente/vuoto = nessun tier proprio → il chiamante fa fallback al listino
   *  statico excursions.pricing_tiers. Vedi tiersForDate(). */
  pricing_tiers?: ExcursionPricingTier[];
}

/** True se `md` ('MM-DD') cade nella finestra [start, end], gestendo il wrap
 *  capodanno (start > end → finestra a cavallo Dic/Gen). */
export function mdInWindow(md: string, startMd: string, endMd: string): boolean {
  if (!startMd || !endMd) return true;
  return startMd <= endMd
    ? md >= startMd && md <= endMd
    : md >= startMd || md <= endMd;
}

/** 'MM-DD' valido (01-12 / 01-31, validazione sintattica). */
export function isValidMd(s: unknown): s is string {
  return typeof s === 'string' && /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(s);
}

/** Costruisce 'MM-DD' da componenti mese/giorno (1-based). */
export function toMd(month: number, day: number): string {
  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Intervalli MM-DD di un periodo: `ranges` se valorizzato, altrimenti il
 *  singolo start_md/end_md (retrocompat). */
export function periodRanges(p: SeasonalPeriod): MdRange[] {
  if (Array.isArray(p.ranges) && p.ranges.length > 0) return p.ranges;
  if (p.start_md && p.end_md) return [{ start_md: p.start_md, end_md: p.end_md }];
  return [];
}

/** True se `md` cade in QUALSIASI intervallo del periodo. */
export function mdInPeriod(md: string, p: SeasonalPeriod): boolean {
  return periodRanges(p).some((r) => mdInWindow(md, r.start_md, r.end_md));
}

/** Slot applicabili in una data ('MM-DD'): primo periodo che la contiene.
 *  Nessun match → []. */
export function slotsForDate(periods: SeasonalPeriod[], md: string): SeasonalSlot[] {
  for (const p of periods) {
    if (mdInPeriod(md, p)) {
      return Array.isArray(p.slots) ? p.slots : [];
    }
  }
  return [];
}

/** Modalità prezzo per-fascia (escursioni). Boats/undefined → prezzo condiviso. */
export type PriceMode = 'per_person' | 'per_group' | 'tiered' | 'quote_on_request';

/** Prezzo della FASCIA per la modalità data: campo per-modalità con fallback al
 *  `price` LEGACY condiviso (dati pre-split). Senza `mode` → `price`. 0/undefined =
 *  non prezzato → null. Vedi [[rule_pricing_season_fascia_price]]. */
export function slotPriceForMode(slot: SeasonalSlot | undefined, mode?: PriceMode): number | null {
  if (!slot) return null;
  const specific = mode === 'per_person' ? slot.price_per_person
    : mode === 'per_group' ? slot.price_group
    : undefined;
  const v = specific != null && Number(specific) > 0 ? Number(specific)
    : (slot.price != null && Number(slot.price) > 0 ? Number(slot.price) : null);
  return v;
}

/** Prezzo della FASCIA (granularità): prezzo dello slot con `start_time` nel periodo
 *  che copre la data, risolto per `mode` (per_person/per_group → campo dedicato,
 *  fallback `price`). null = nessuna fascia con quell'orario o senza prezzo (>0) →
 *  fallback al prezzo stagione/base. Vedi [[feedback_granularity_priority]]. */
export function slotPriceForDate(periods: SeasonalPeriod[], md: string, startTime: string, mode?: PriceMode): number | null {
  const s = slotsForDate(periods, md).find((x) => x.start_time === startTime);
  return slotPriceForMode(s, mode);
}

/** Prezzo minimo tra le fasce prezzate del giorno (per il "a partire da" catalogo). */
export function minSlotPriceForDate(periods: SeasonalPeriod[], md: string): number | null {
  const priced = slotsForDate(periods, md).map((x) => Number(x.price) || 0).filter((v) => v > 0);
  return priced.length ? Math.min(...priced) : null;
}

/** Prezzo minimo REALE mode-aware su TUTTE le stagioni/fasce (per il "da €" catalogo).
 *  Modello Stagione→Fascia→prezzo: scorre tutti gli slot di tutti i periodi e prende
 *  il minimo secondo la modalità:
 *   - per_person/per_group → min di slotPriceForMode(slot, mode) (fallback slot.price);
 *   - tiered → min dei prezzi tier da slot.pricing_tiers ?? period.pricing_tiers;
 *  Fallback: se nessuno slot è prezzato, ripiega su period.price (season-level).
 *  null = nessun prezzo trovato → il chiamante usa il prezzo base statico. */
export function nextSeasonalMinForMode(periods: SeasonalPeriod[], mode?: PriceMode): number | null {
  const vals: number[] = [];
  for (const p of periods) {
    const slots = Array.isArray(p.slots) ? p.slots : [];
    if (mode === 'tiered') {
      for (const s of slots) {
        const tiers = (Array.isArray(s.pricing_tiers) && s.pricing_tiers.length ? s.pricing_tiers : p.pricing_tiers) || [];
        for (const t of tiers) { const v = Number(t.price) || 0; if (v > 0) vals.push(v); }
      }
    } else {
      for (const s of slots) { const v = slotPriceForMode(s, mode); if (v != null && v > 0) vals.push(v); }
    }
    // Fallback season-level se le fasce non hanno prezzi per questa modalità.
    if (p.price != null && Number(p.price) > 0) vals.push(Number(p.price));
  }
  return vals.length ? Math.min(...vals) : null;
}

/** Prezzo stagionale per una data ('MM-DD'): primo periodo che la contiene con
 *  un `price` valido > 0. Nessun match → null (fallback al prezzo fisso).
 *  `half=true` ritorna price_half_day se presente. */
export function priceForDate(periods: SeasonalPeriod[], md: string, half = false): number | null {
  for (const p of periods) {
    if (!mdInPeriod(md, p)) continue;
    const v = half ? (p.price_half_day ?? p.price) : p.price;
    if (v != null && Number(v) > 0) return Number(v);
  }
  return null;
}

/** Tariffe per numero persone (pricing_mode='tiered') per una data ('MM-DD') e,
 *  opzionalmente, una fascia oraria ('HH:MM'). Gerarchia Stagione→Fascia→prezzo:
 *   1. fascia: se `startTime` dato, i tier dello slot con quel start_time vincono;
 *   2. stagione: altrimenti i tier season-level (`period.pricing_tiers`);
 *   3. null → il chiamante fa fallback al listino statico excursions.pricing_tiers.
 *  Senza `startTime` = solo livello stagione (retrocompat chiamanti esistenti). */
export function tiersForDate(periods: SeasonalPeriod[], md: string, startTime?: string): ExcursionPricingTier[] | null {
  for (const p of periods) {
    if (!mdInPeriod(md, p)) continue;
    // Fascia oraria (più specifico): tier dello slot con quel start_time.
    if (startTime) {
      const slot = (Array.isArray(p.slots) ? p.slots : []).find((s) => s.start_time === startTime);
      if (slot && Array.isArray(slot.pricing_tiers) && slot.pricing_tiers.length > 0) return slot.pricing_tiers;
    }
    // Stagione (fallback): tier season-level.
    if (Array.isArray(p.pricing_tiers) && p.pricing_tiers.length > 0) return p.pricing_tiers;
  }
  return null;
}

/**
 * Risoluzione CASCATA delle fasce orarie per la generazione partenze:
 * `Evento ?? Barca ?? Stagione/Azienda` (più specifico vince).
 *
 *  - EVENTO (escursione): se l'escursione NON eredita (`excursionInherited=false`)
 *    e ha periodi propri non vuoti → vincono.
 *  - BARCA: altrimenti, se la barca collegata ha periodi non vuoti → vincono.
 *  - STAGIONE/AZIENDA: altrimenti, se l'azienda (owner) ha periodi non vuoti → vincono.
 *  - FALLBACK: i periodi propagati/legacy dell'escursione (anche se inherited),
 *    così non si rompe il comportamento esistente quando barca/azienda sono vuote.
 *
 * Ogni livello è normalizzato con `sanitizePeriods` (input grezzo da DB/Json).
 */
export function resolveEffectivePeriods(args: {
  excursionPeriods: unknown;
  excursionInherited: boolean;
  boatPeriods: unknown;
  companyPeriods: unknown;
}): SeasonalPeriod[] {
  const excursion = sanitizePeriods(args.excursionPeriods);
  const boat = sanitizePeriods(args.boatPeriods);
  const company = sanitizePeriods(args.companyPeriods);

  // Evento (override esplicito): l'escursione non eredita e ha fasce proprie.
  if (!args.excursionInherited && excursion.length > 0) return excursion;
  // Barca.
  if (boat.length > 0) return boat;
  // Stagione/Azienda.
  if (company.length > 0) return company;
  // Fallback: periodi propagati/legacy sull'escursione.
  return excursion;
}

/** Normalizza/valida una tabella tier grezza (persons>0, price>=0). Riusata per
 *  i tier season-level e per-fascia (slot). */
export function sanitizePricingTiers(raw: unknown): ExcursionPricingTier[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((t): t is Record<string, unknown> => !!t && typeof t === 'object')
    .filter((t) => Number(t.persons) > 0 && Number.isFinite(Number(t.price)) && Number(t.price) >= 0)
    .map((t) => ({
      persons: Math.floor(Number(t.persons)),
      price: Number(t.price),
      ...(typeof t.label === 'string' ? { label: t.label.slice(0, 60) } : {}),
      ...(t.exclusive === true ? { exclusive: true } : {}),
    }));
}

/** Normalizza/valida un array di periodi grezzo (da body/DB). Scarta periodi
 *  con MD invalidi o senza slot validi. */
export function sanitizePeriods(raw: unknown): SeasonalPeriod[] {
  if (!Array.isArray(raw)) return [];
  const out: SeasonalPeriod[] = [];
  for (const p of raw) {
    if (!p || typeof p !== 'object') continue;
    const o = p as Record<string, unknown>;
    // Intervalli: da `ranges[]` (canonico) o fallback al singolo start_md/end_md.
    const rawRanges: Array<{ start_md?: unknown; end_md?: unknown }> = Array.isArray(o.ranges) && o.ranges.length > 0
      ? (o.ranges as Array<{ start_md?: unknown; end_md?: unknown }>)
      : [{ start_md: o.start_md, end_md: o.end_md }];
    const ranges: MdRange[] = rawRanges
      .filter((r) => r && isValidMd(r.start_md) && isValidMd(r.end_md))
      .map((r) => ({ start_md: r.start_md as string, end_md: r.end_md as string }));
    if (ranges.length === 0) continue;
    const slots = Array.isArray(o.slots)
      ? o.slots
          .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
          .filter((s) => /^\d{2}:\d{2}$/.test(String((s as Record<string, unknown>).start_time || '')))
          .map((s) => {
            const slotTiers = sanitizePricingTiers(s.pricing_tiers);
            const pos = (v: unknown) => (Number(v) > 0 ? Number(v) : undefined);
            const pp = pos(s.price_per_person); const pg = pos(s.price_group); const pr = pos(s.price);
            return {
              start_time: String(s.start_time),
              duration_minutes: Number(s.duration_minutes) || 0,
              capacity: Number(s.capacity) || 0,
              ...(typeof s.id === 'string' ? { id: s.id } : {}),
              ...(typeof s.label === 'string' ? { label: s.label.slice(0, 40) } : {}),
              ...(s.status === 'deprecated' || s.status === 'archived' ? { status: s.status as 'deprecated' | 'archived' } : {}),
              ...(pr != null ? { price: pr } : {}),
              ...(pp != null ? { price_per_person: pp } : {}),
              ...(pg != null ? { price_group: pg } : {}),
              ...(slotTiers.length > 0 ? { pricing_tiers: slotTiers } : {}),
            };
          })
      : [];
    if (slots.length === 0) continue;
    const price = Number(o.price);
    const priceHalf = Number(o.price_half_day);
    const pricingTiers = sanitizePricingTiers(o.pricing_tiers);
    out.push({
      label: typeof o.label === 'string' ? o.label.trim().slice(0, 60) : undefined,
      ranges,
      start_md: ranges[0].start_md, // mirror primo range (retrocompat display)
      end_md: ranges[0].end_md,
      slots,
      ...(Number.isFinite(price) && price > 0 ? { price } : {}),
      ...(Number.isFinite(priceHalf) && priceHalf > 0 ? { price_half_day: priceHalf } : {}),
      ...(pricingTiers.length > 0 ? { pricing_tiers: pricingTiers } : {}),
    });
  }
  return out;
}
